/**
 * Fewer choices for a child who is finding it hard (D-187).
 *
 * The content gives later books more options and more wrong words
 * ([D-185](../../../docs/DECISIONS.md#d-185)). The band says how much help *this
 * child* needs right now ([Adaptive engine](../../../docs/ADAPTIVE-ENGINE.md#difficulty-bands)):
 * a Sprout sees two options and word banks with no wrong words, a Sapling three
 * options and one wrong word, and from Grove up everything the content has.
 *
 * Trimmed, never added to: a Book 1 exercise with two options stays at two for a
 * Summit child. What is kept is the right answer and the wrong answers in the
 * order they were written, because the authored ones come first and are the
 * careful ones (*long curly hair* against *long straight hair*).
 */
/** The most options a multiple choice shows at each band. */
export const OPTIONS_AT_BAND = {
    1: 2,
    2: 3,
    3: 4,
    4: 4,
    5: 4,
};
/** The most wrong words a word bank or a paragraph's bank holds at each band. */
export const WRONG_WORDS_AT_BAND = {
    1: 0,
    2: 1,
    3: Number.POSITIVE_INFINITY,
    4: Number.POSITIVE_INFINITY,
    5: Number.POSITIVE_INFINITY,
};
const CHOICES = new Set(['listen_choose', 'meaning_choose', 'picture_choice']);
const BANKS = {
    word_bank_order: 'decoys',
    build_sentence: 'decoys',
    gap_text: 'distractors',
};
const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
/**
 * The payload as a child at `band` should see it.
 *
 * Anything this does not recognise comes back untouched: a payload that is not
 * a choice or a bank has nothing to trim, and one that is malformed is the
 * renderer's to refuse, not this function's to guess at.
 */
export function scaffoldFor(band, payload) {
    if (!isRecord(payload) || typeof payload['type'] !== 'string')
        return payload;
    const type = payload['type'];
    if (CHOICES.has(type) && Array.isArray(payload['options'])) {
        const options = payload['options'];
        const most = OPTIONS_AT_BAND[band];
        if (options.length <= most)
            return payload;
        const right = options.filter((o) => isRecord(o) && o['correct'] === true);
        // Not exactly one right answer is content the linter refuses; leave it be.
        if (right.length !== 1)
            return payload;
        const wrong = options.filter((o) => !(isRecord(o) && o['correct'] === true));
        const kept = new Set([...right, ...wrong.slice(0, most - 1)]);
        return { ...payload, options: options.filter((o) => kept.has(o)) };
    }
    const key = BANKS[type];
    if (key !== undefined && Array.isArray(payload[key])) {
        const wrong = payload[key];
        const most = WRONG_WORDS_AT_BAND[band];
        if (wrong.length <= most)
            return payload;
        return { ...payload, [key]: wrong.slice(0, most) };
    }
    return payload;
}
//# sourceMappingURL=scaffold.js.map